import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { COLORS } from '../theme/themeTokens';

interface AccountBalancePanelProps {
  /** Formatted amount string, e.g. "2,864,758.25" */
  amount: string;
  /** Currency ticker, e.g. "USDT" */
  currency: string;
  /** Account label shown below the amount, e.g. "Current account".
   *  Omit / empty → no subtitle is rendered. */
  accountTitle?: string;
  /** Formatted spendable amount, e.g. "381 757,95 USDT". Pass it only when
   *  part of the balance is held by a pending request; the panel then shows
   *  the whole balance above and what can be moved below. */
  available?: string;
  loading?: boolean;
  /** Per-instance container override (e.g. to tweak paddingTop /
   *  paddingBottom on screens that need a specific gap to neighbouring
   *  elements). Stacked AFTER the default styles so any property the
   *  override sets wins. */
  style?: StyleProp<ViewStyle>;
}

/**
 * Balance panel for a single account.
 * Renders amount + currency on a dark navy background (matching SimpleHeader),
 * with the account name centred below the amount.
 */
const AccountBalancePanel: React.FC<AccountBalancePanelProps> = ({
  amount,
  currency,
  accountTitle,
  available,
  loading = false,
  style,
}) => {
  return (
    <View style={[styles.container, style]}>
      {loading ? (
        <Text style={styles.amount}>...</Text>
      ) : (
        <Text style={styles.amount} numberOfLines={1} adjustsFontSizeToFit>
          {amount}
          <Text style={styles.amountCurrency}>{` ${currency}`}</Text>
        </Text>
      )}
      {!loading && available ? (
        <Text style={styles.availableText}>{`Available ${available}`}</Text>
      ) : null}
      {accountTitle ? <Text style={styles.accountTitle}>{accountTitle}</Text> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primaryNavy,
    paddingHorizontal: 20,
    paddingTop: 8,
    paddingBottom: 20,
    alignItems: 'center',
    gap: 8,
  },
  availableText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 14,
    fontWeight: '500',
  },
  amount: {
    color: COLORS.white,
    fontSize: 32,
    fontWeight: '600',
    letterSpacing: 0.5,
    textAlign: 'center',
  },
  amountCurrency: {
    color: COLORS.blueSteel,
    fontSize: 22,
    fontWeight: '600',
    letterSpacing: 0.5,
  },
  accountTitle: {
    color: COLORS.blueSteel,
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
  },
});

export default AccountBalancePanel;
